import { nextTick, ref } from 'vue';

import type { KaToastItem } from '@/kasten-components/components/KaToaster';

export interface UseToastOptions {
  maxToasts?: number;
}

export interface ToastOptions extends Partial<Omit<KaToastItem, 'id'>> {
  id?: string | number;
}

const toasts = ref<KaToastItem[]>([]);
const maxToasts = ref(5);
const running = ref(false);
const queue: KaToastItem[] = [];

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

async function processQueue() {
  if (running.value || queue.length === 0) {
    return;
  }

  running.value = true;

  while (queue.length > 0) {
    const toast = queue.shift()!;
    await nextTick();
    toasts.value = [...toasts.value, toast].slice(-maxToasts.value);
  }

  running.value = false;
}

function add(options: ToastOptions): KaToastItem {
  const toast: KaToastItem = {
    id: options.id || generateId(),
    open: true,
    color: 'primary',
    duration: 5000,
    closable: true,
    showProgressBar: true,
    ...options,
  };

  queue.push(toast);
  processQueue();

  return toast;
}

function update(id: string | number, options: Omit<Partial<KaToastItem>, 'id'>) {
  const index = toasts.value.findIndex((t) => t.id === id);
  if (index !== -1) {
    toasts.value[index] = {
      ...toasts.value[index],
      ...options,
    };
  }
}

function remove(id: string | number) {
  const index = toasts.value.findIndex((t) => t.id === id);
  if (index !== -1) {
    toasts.value[index] = {
      ...toasts.value[index],
      open: false,
    };

    // Remove from array after animation
    setTimeout(() => {
      toasts.value = toasts.value.filter((t) => t.id !== id);
    }, 300);
  }
}

function clear() {
  toasts.value = [];
  queue.length = 0;
}

// Convenience methods for different toast types
function success(options: ToastOptions) {
  return add({ ...options, color: 'success' });
}

function error(options: ToastOptions) {
  return add({ ...options, color: 'error' });
}

function warning(options: ToastOptions) {
  return add({ ...options, color: 'warning' });
}

function info(options: ToastOptions) {
  return add({ ...options, color: 'primary' });
}

function persistent(options: ToastOptions) {
  return add({
    duration: 0, // Disable auto-closing
    showProgressBar: false, // Hide progress bar since there's no timer
    ...options,
  });
}

export function useToast(options: UseToastOptions = {}) {
  if (options.maxToasts !== undefined) {
    maxToasts.value = options.maxToasts;
  }

  return {
    toasts,
    add,
    update,
    remove,
    clear,
    success,
    error,
    warning,
    info,
    persistent,
  };
}
